/** Default work schedule: Monday–Friday (Python weekday numbers Mon=0 .. Sun=6). */
export const DEFAULT_WORK_DAYS = [0, 1, 2, 3, 4]

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/** Python weekday (Mon=0 .. Sun=6) for an ISO `YYYY-MM-DD` date string. */
export function pyWeekday(dateStr: string): number {
  const d = new Date(dateStr + 'T00:00:00')
  return (d.getDay() + 6) % 7 // JS getDay() Sun=0..Sat=6 -> Mon=0..Sun=6
}

/**
 * True when the date is NOT one of the employee's working weekdays, so it
 * should be styled like a weekend/off day. Falls back to Mon–Fri when no
 * schedule is provided.
 */
export function isOffDay(dateStr: string, workDays?: number[]): boolean {
  const wd = workDays && workDays.length ? workDays : DEFAULT_WORK_DAYS
  return !wd.includes(pyWeekday(dateStr))
}

/** Human label for a list of Python weekday numbers, e.g. [4,5,6,0] -> "Mon, Fri, Sat, Sun". */
export function formatWorkDays(workDays?: number[]): string {
  const wd = workDays && workDays.length ? workDays : DEFAULT_WORK_DAYS
  return [...wd]
    .filter((d) => d >= 0 && d <= 6)
    .sort((a, b) => a - b)
    .map((d) => WEEKDAY_LABELS[d])
    .join(', ')
}

// ---------------------------------------------------------------------------
// "Copy worked time to all working days" logic
// ---------------------------------------------------------------------------

/** The worked-time clock fields that get copied across working days. */
export interface WorkedTimeFields {
  clock_in_1: string
  clock_out_1: string
  clock_in_2: string
  clock_out_2: string
}

/** Minimal per-day shape the copy logic needs: a date plus the worked-time fields. */
export type CopyableDay = WorkedTimeFields & { date: string }

/** Extract only the worked-time clock fields from a fuller day record. */
export function extractWorkedTime(day: WorkedTimeFields): WorkedTimeFields {
  return {
    clock_in_1: day.clock_in_1,
    clock_out_1: day.clock_out_1,
    clock_in_2: day.clock_in_2,
    clock_out_2: day.clock_out_2,
  }
}

/** True when a day has any worked-time clock value set. */
export function hasWorkedTime(day: WorkedTimeFields): boolean {
  return Boolean(day.clock_in_1 || day.clock_out_1 || day.clock_in_2 || day.clock_out_2)
}

/**
 * Indices of the scheduled WORKING days (other than the source) whose existing
 * worked-time would be overwritten by a copy from the source day.
 *
 * A target counts as "would overwrite" when it currently has any worked-time
 * value AND that value differs from the source's worked-time. Non-working days
 * and the source day itself are never included.
 *
 * Pure / side-effect free.
 */
export function findOverwriteTargets(
  days: CopyableDay[],
  sourceIndex: number,
  workDays?: number[],
): number[] {
  const source = days[sourceIndex]
  if (!source) return []
  const src = extractWorkedTime(source)
  const targets: number[] = []
  days.forEach((day, i) => {
    if (i === sourceIndex) return
    if (isOffDay(day.date, workDays)) return
    if (!hasWorkedTime(day)) return
    const cur = extractWorkedTime(day)
    const same =
      cur.clock_in_1 === src.clock_in_1 &&
      cur.clock_out_1 === src.clock_out_1 &&
      cur.clock_in_2 === src.clock_in_2 &&
      cur.clock_out_2 === src.clock_out_2
    if (!same) targets.push(i)
  })
  return targets
}

/**
 * Copy the worked-time fields (clock in/out 1 & 2 — and therefore Hours Worked,
 * which is derived from them) from the day at `sourceIndex` onto every other
 * scheduled WORKING day in the period.
 *
 * Returns a NEW array; the input is not mutated. For each day:
 *   - The source day is returned unchanged.
 *   - Non-working days (per `workDays`) are returned unchanged — they are never
 *     touched.
 *   - Working days get the source's worked-time copied in, while ALL of their
 *     other fields (Holiday hours, PTO/vacation_hours, comment, etc.) are
 *     preserved exactly. Holiday and PTO are intentionally NOT copied — they
 *     remain per-day exceptions.
 *
 * Pure / side-effect free.
 */
export function copyWorkedTimeToWorkingDays<T extends CopyableDay>(
  days: T[],
  sourceIndex: number,
  workDays?: number[],
): T[] {
  const source = days[sourceIndex]
  if (!source) return days.map((d) => ({ ...d }))
  const src = extractWorkedTime(source)
  return days.map((day, i) => {
    if (i === sourceIndex) return { ...day }
    if (isOffDay(day.date, workDays)) return { ...day }
    // Working day: overlay worked-time only, preserving everything else.
    return { ...day, ...src }
  })
}
